// src/services/lessonStoryboardService.js
//
// Saved storyboards for the Lesson video studio, one per topic:
//   lessonStoryboards/{slug}  { title, template, intro, outro, byLine, … }
//
// byLine maps a LYRIC LINE NUMBER (its index in lyricSync.lines) to the scene
// for that line, so a storyboard always fits its song exactly. Admin-only
// (see the lessonStoryboards block in firestore.rules).

import { collection, doc, getDoc, getDocs, serverTimestamp, setDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/firebaseConfig";

export const LESSON_STORYBOARDS = "lessonStoryboards";

export const getAllLessonStoryboards = async () => {
  const snap = await getDocs(collection(db, LESSON_STORYBOARDS));
  const out = {};
  snap.forEach((d) => {
    out[d.id] = { id: d.id, ...d.data() };
  });
  return out;
};

export const getLessonStoryboard = async (slug) => {
  if (!slug) return null;
  const snap = await getDoc(doc(db, LESSON_STORYBOARDS, slug));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

/** Replaces the whole storyboard (not a merge: a deleted scene must stay deleted). */
export const saveLessonStoryboard = async (slug, storyboard) => {
  if (!slug) throw new Error("A storyboard needs a topic slug.");
  const user = getAuth().currentUser;
  const payload = JSON.parse(
    JSON.stringify({
      title: storyboard.title || "",
      template: storyboard.template || "",
      intro: storyboard.intro || {},
      outro: storyboard.outro || {},
      byLine: storyboard.byLine || {},
      // directions sent with every AI picture made for this song
      pictureStyle: storyboard.pictureStyle || "",
      // the picture look for the whole video, picked with buttons
      // (components/video/artStyles.js)
      artStyle: storyboard.artStyle || "",
      background: storyboard.background || "",
      noPeople: Boolean(storyboard.noPeople),
      sameDrawing: Boolean(storyboard.sameDrawing),
      // colour groups: lines with the same colour share ONE picture
      //   groups:     { g1: { color: "red", name, idea, url } }
      //   lineGroups: { "<line #>": "g1" }
      groups: storyboard.groups || {},
      lineGroups: storyboard.lineGroups || {},
    })
  );
  await setDoc(doc(db, LESSON_STORYBOARDS, slug), {
    ...payload,
    slug,
    updatedAt: serverTimestamp(),
    updatedBy: user?.email || user?.uid || "unknown",
  });
  return payload;
};

/* ------------------------------------------------------------------ */
/* Lyric pictures                                                      */
/* ------------------------------------------------------------------ */
//
//   lyricImages/{slug}  { slug, title, lines: { "<line #>": { url, path, text, … } } }
//
// One picture per lyric line, made by src/scripts/make-lyric-images.mjs
// (Gemini, via the Admin SDK). Line numbers are indexes into
// lyricSync/{slug}.lines — the same numbering the storyboard's byLine uses —
// so a picture always belongs to exactly one storyboard row. Admin-only both
// ways (see the lyricImages block in firestore.rules).

export const LYRIC_IMAGES = "lyricImages";

/** { slug: { lines: { i: { url, … } }, … } }. Empty if the rule isn't deployed yet. */
export const getAllLyricImages = async () => {
  try {
    const snap = await getDocs(collection(db, LYRIC_IMAGES));
    const out = {};
    snap.forEach((d) => {
      out[d.id] = { id: d.id, ...d.data() };
    });
    return out;
  } catch (err) {
    // Pictures are an extra. A missing rule must not stop the studio opening.
    if (err?.code === "permission-denied") return {};
    throw err;
  }
};

/** The pictures for one song as { lineIndex: url }, newest wins. */
export const lyricImageUrls = (doc) => {
  const out = {};
  Object.entries(doc?.lines || {}).forEach(([i, v]) => {
    if (v?.url) out[Number(i)] = v.url;
  });
  return out;
};

/** Records one lyric line's picture in lyricImages/{slug} (merge — other lines stay). */
export const saveLyricImage = async (slug, lineIndex, pic) => {
  const user = getAuth().currentUser;
  await setDoc(
    doc(db, LYRIC_IMAGES, slug),
    {
      slug,
      ...(pic.title ? { title: pic.title } : {}),
      lines: {
        [lineIndex]: {
          url: pic.url,
          path: pic.path || "",
          text: pic.text || "",
          model: pic.model || "",
          createdAt: new Date().toISOString(),
          createdBy: user?.email || user?.uid || "unknown",
        },
      },
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
};

/**
 * The words sent to the Image studio's generateImage function for one lyric
 * line. Kept under its 2,000-character limit. The function adds the SBL style
 * and the "for K-8 students, no words in the picture" note itself.
 */
export const linePicturePrompt = ({
  title,
  line,
  before = [],
  after = [],
  idea = "",
  songStyle = "",
  look = "",
  follow = false,
  hasRef = false,
  alsoSung = [],
}) => {
  const q = (s) => `"${String(s).slice(0, 120)}"`;
  const yours = Boolean(idea.trim() || songStyle.trim());
  const others = alsoSung.filter((x) => String(x).trim() && String(x).trim() !== String(line).trim());
  const parts = [
    `A picture for one line of "${title}", a children's learning song. It is shown in an animated lesson video while this line is sung: ${q(line)}.`,
    others.length ? `The same picture is also shown on: ${others.slice(0, 3).map(q).join(" / ")}.` : "",
    before.length || after.length
      ? `Lines around it, for context only: ${before.map(q).join(" / ") || "(start)"} → [this line] → ${after.map(q).join(" / ") || "(end)"}.`
      : "",
    follow && hasRef
      ? "The attached picture is the drawing this one must match (the scene before it, or the video's main drawing). Redraw that SAME picture — same objects, layout, colours, style and background — and change ONLY what the directions below say, so the two pictures play like one animation."
      : "",
    songStyle.trim() ? `Directions for every picture in this song (follow these): ${songStyle.trim().slice(0, 500)}` : "",
    look.trim() ? look.trim().slice(0, 700) : "",
    idea.trim()
      ? `What THIS picture must show (follow this exactly): ${idea.trim().slice(0, 600)}`
      : "Show what this line means in ONE clear, simple scene a child understands at a glance.",
    yours
      ? ""
      : "If the line is about an amount, counting, adding, taking away, groups, shapes, measuring, money, fractions or time, show real things a child can count (apples, blocks, coins, cookies…), with every amount exactly right and neatly arranged.",
    yours
      ? "No numbers, digits, letters or words in the picture unless the directions above ask for them."
      : "No numbers, digits, letters, equations or words anywhere in the picture.",
    "Keep the main subject in the middle and fill the frame with it; keep the bottom-left and bottom-right corners plain, because children may stand in front of them in the video. Wide picture.",
    hasRef && !follow
      ? "Match the attached picture's art style and colours so the song's pictures look like one set, but draw a NEW scene for this line."
      : "",
  ];
  return parts.filter(Boolean).join("\n\n").slice(0, 1990);
};

/**
 * Makes one AI picture for a lyric line with the Image studio's generateImage
 * Cloud Function (admins only), and records it in lyricImages/{slug}.
 * Resolves { url, path }.
 */
export const makeLinePicture = async ({
  slug,
  title,
  lineIndex,
  line,
  before,
  after,
  idea,
  songStyle,
  look = "",
  ownStyle = false,
  alsoSung = [],
  alsoLines = [],
  follow = false,
  refUrl,
  quality = "standard",
}) => {
  // The function can take up to 150 s; the browser's default wait is 70 s.
  const call = httpsCallable(functions, "generateImage", { timeout: 170000 });
  const prompt = linePicturePrompt({
    title,
    line,
    before,
    after,
    idea,
    songStyle,
    look,
    follow,
    hasRef: Boolean(refUrl),
    alsoSung,
  });
  let res;
  try {
    res = await call({
      prompt,
      // A style picked with the style buttons is in the prompt itself; the
      // function's own SBL cartoon style would fight it, so it is left off.
      style: ownStyle ? "none" : "sbl",
      model: quality,
      aspectRatio: "16:9",
      name: `${slug}-line-${lineIndex}`,
      refs: refUrl ? [refUrl] : [],
    });
  } catch (err) {
    const code = String(err?.code || "");
    if (code.includes("not-found") && /generateImage|INTERNAL|not found/i.test(err?.message || "")) {
      throw new Error("The picture maker (generateImage) isn't deployed. See the Image studio setup: firebase deploy --only functions:generateImage");
    }
    if (code.includes("permission-denied")) throw new Error("Only SBL admin accounts can make pictures.");
    if (code.includes("deadline-exceeded")) throw new Error("The picture took too long. Press the button again.");
    throw new Error(err?.message || String(err));
  }
  const { url, path } = res.data || {};
  if (!url) throw new Error("No picture came back. Try describing it differently.");
  await saveLyricImage(slug, lineIndex, { url, path, text: line, title, model: quality });
  // a colour group: every line in it keeps the picture too
  for (const o of alsoLines) {
    if (o && o.i !== lineIndex) {
      // eslint-disable-next-line no-await-in-loop
      await saveLyricImage(slug, o.i, { url, path, text: o.text, title, model: quality });
    }
  }
  return { url, path, prompt };
};

/** A short fingerprint of a storyboard, to spot a video made from an older one. */
export const storyboardSignature = (storyboard) => {
  const text = JSON.stringify({
    t: storyboard?.title || "",
    i: storyboard?.intro || {},
    o: storyboard?.outro || {},
    b: storyboard?.byLine || {},
  });
  let h = 5381;
  for (let k = 0; k < text.length; k += 1) h = ((h << 5) + h + text.charCodeAt(k)) | 0;
  return (h >>> 0).toString(36);
};
